import { useCallback, useState, useEffect } from 'react'
import type { RefObject } from 'react'

/**
 * Hook for inserting emojis at cursor position in textareas with proper state synchronization.
 *
 * Features:
 * - Inserts emoji at current cursor position
 * - Maintains cursor position after text update using React state synchronization
 *
 * @param textareaRef - Ref to the textarea element
 * @param text - Current text content
 * @param setText - Function to update text content
 * @returns Function that inserts emoji at cursor position
 *
 * @example
 * ```tsx
 * const handleEmojiSelect = useTextareaEmojiInsert(textareaRef, text, setText)
 * <EmojiPicker onEmojiSelect={handleEmojiSelect} />
 * ```
 */
export const useTextareaEmojiInsert = (
  textareaRef: RefObject<HTMLTextAreaElement>,
  text: string,
  setText: (text: string) => void,
): ((emoji: string) => void) => {
  const [pendingCursorPosition, setPendingCursorPosition] = useState<
    number | null
  >(null)

  // Set cursor position when text changes and we have a pending position
  useEffect(() => {
    if (textareaRef.current && pendingCursorPosition !== null) {
      try {
        textareaRef.current.setSelectionRange(
          pendingCursorPosition,
          pendingCursorPosition,
        )
        textareaRef.current.focus()
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn(
            'Failed to set cursor position, falling back to focus only:',
            error,
          )
        }
        // Fallback: just focus without setting selection
        textareaRef.current.focus()
      }
      setPendingCursorPosition(null)
    }
  }, [text, pendingCursorPosition, textareaRef])

  return useCallback(
    (emoji: string) => {
      const textarea = textareaRef.current
      if (!textarea) return

      const cursorPosition = textarea.selectionStart
      const textBeforeCursor = text.substring(0, cursorPosition)
      const textAfterCursor = text.substring(cursorPosition)

      const newText = textBeforeCursor + emoji + ' ' + textAfterCursor
      const newCursorPosition = cursorPosition + emoji.length + 1

      // Update text and set pending cursor position
      setText(newText)
      setPendingCursorPosition(newCursorPosition)
    },
    [textareaRef, text, setText],
  )
}
